import React, { useCallback, useEffect, useState } from 'react';
import { useAppDataContext } from '../context/AppDataContext';
import { supabase } from '../supabaseClient';
import { Building2, Users, AlertTriangle, TrendingUp, UserCheck, ShieldAlert, BarChart3, PieChart, Activity } from 'lucide-react';
import type { Profile } from '../types';

interface TherapistStats {
    profile: Profile;
    patientCount: number;
    highRiskCount: number;
    avgStreak: number;
}

const ClinicAdminDashboard: React.FC = () => {
    const { profile, clinic } = useAppDataContext();
    const [therapistsStats, setTherapistsStats] = useState<TherapistStats[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchClinicData = useCallback(async () => {
        try {
            setLoading(true);
            
            // 1. Fetch all therapists in the clinic
            const { data: therapists, error: therapistsErr } = await supabase
                .from('profiles')
                .select('*')
                .eq('role', 'therapist')
                .eq('clinic_id', clinic!.id);
            
            if (therapistsErr) throw therapistsErr;

            if (!therapists || therapists.length === 0) {
                setTherapistsStats([]);
                return;
            }

            const therapistIds = therapists.map(t => t.id);

            // 2. Fetch all patients belonging to these therapists
            const { data: patients, error: patientsErr } = await supabase
                .from('profiles')
                .select('*')
                .eq('role', 'patient')
                .in('therapist_id', therapistIds);

            if (patientsErr) throw patientsErr;

            // 3. Aggregate data per therapist
            const stats: TherapistStats[] = therapists.map(t => {
                const tPatients = (patients || []).filter(p => p.therapist_id === t.id);
                
                const now = new Date();
                const highRiskCount = tPatients.filter(p => {
                    if (p.churn_risk_level === 'high') return true;
                    if (!p.last_seen) return true;
                    const diffTime = Math.abs(now.getTime() - new Date(p.last_seen).getTime());
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays >= 4;
                }).length;

                const totalStreak = tPatients.reduce((acc, p) => acc + (p.streak || 0), 0);
                const avgStreak = tPatients.length > 0 ? parseFloat((totalStreak / tPatients.length).toFixed(1)) : 0;

                return {
                    profile: t,
                    patientCount: tPatients.length,
                    highRiskCount,
                    avgStreak
                };
            });

            // Sort by most patients
            stats.sort((a, b) => b.patientCount - a.patientCount);
            setTherapistsStats(stats);

        } catch (err) {
            console.error('Error fetching clinic data:', err);
        } finally {
            setLoading(false);
        }
    }, [clinic]);

    useEffect(() => {
        if (profile?.is_clinic_admin && clinic?.id) {
            fetchClinicData();
        } else {
            setLoading(false);
        }
    }, [profile, clinic, fetchClinicData]);

    if (!profile?.is_clinic_admin) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-background p-6 text-center dir-rtl">
                <ShieldAlert size={48} className="text-red-500 mb-4" />
                <h2 className="text-xl font-bold text-foreground">אין גישה</h2>
                <p className="text-muted-foreground mt-2">אזור זה מיועד למנהלי מרפאה בלבד.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    const totalTherapists = therapistsStats.length;
    const totalPatients = therapistsStats.reduce((acc, t) => acc + t.patientCount, 0);
    const totalHighRisk = therapistsStats.reduce((acc, t) => acc + t.highRiskCount, 0);

    return (
        <div className="flex flex-col h-full bg-background relative dir-rtl transition-colors duration-300">
            <header className="p-6 pt-10 bg-card border-b border-border flex justify-between items-center z-10">
                <div>
                    <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                        <Building2 className="text-primary" /> ניהול קליניקה
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">{clinic?.name || 'מרפאה לא מוגדרת'}</p>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto p-6 pb-24 scrollbar-hide space-y-6">
                
                {/* Investor Stats Section */}
                <div className="bg-gradient-to-br from-secondary/30 to-card p-5 rounded-2xl border border-primary/20 shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl"></div>
                    
                    <div className="flex justify-between items-center mb-4 relative z-10">
                        <h2 className="text-foreground font-bold text-lg flex items-center gap-2">
                            <BarChart3 className="text-primary" /> נתוני צמיחה (Investor KPIs)
                        </h2>
                        <span className="bg-primary/15 text-primary text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wider border border-primary/20">
                            Live Data
                        </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 relative z-10">
                        <div className="bg-card/50 p-3 rounded-xl border border-border/50 backdrop-blur-sm">
                            <div className="flex items-center gap-2 mb-1 text-muted-foreground">
                                <TrendingUp size={14} className="text-emerald-400" />
                                <span className="text-xs font-medium">צמיחה (MoM)</span>
                            </div>
                            <div className="text-xl font-bold text-foreground">+24.5%</div>
                        </div>
                        <div className="bg-card/50 p-3 rounded-xl border border-border/50 backdrop-blur-sm">
                            <div className="flex items-center gap-2 mb-1 text-muted-foreground">
                                <PieChart size={14} className="text-primary" />
                                <span className="text-xs font-medium">מעורבות מטופלים</span>
                            </div>
                            <div className="text-xl font-bold text-foreground">88%</div>
                        </div>
                        <div className="col-span-2 bg-card/50 p-3 rounded-xl border border-border/50 backdrop-blur-sm flex justify-between items-center">
                            <div>
                                <div className="flex items-center gap-2 mb-1 text-muted-foreground">
                                    <Activity size={14} className="text-pink-400" />
                                    <span className="text-xs font-medium">הפחתת נטישה (Churn) בזכות AI</span>
                                </div>
                                <div className="text-xl font-bold text-foreground">35% ירידה</div>
                            </div>
                            <div className="w-10 h-10 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin flex items-center justify-center">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Aggregate Stats */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-card p-4 rounded-2xl border border-border shadow-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="bg-primary/20 p-2 rounded-lg text-primary">
                                <UserCheck size={20} />
                            </div>
                            <span className="text-sm text-muted-foreground font-medium">מטפלים</span>
                        </div>
                        <div className="text-2xl font-bold text-foreground">{totalTherapists}</div>
                    </div>

                    <div className="bg-card p-4 rounded-2xl border border-border shadow-lg">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="bg-emerald-500/20 p-2 rounded-lg text-emerald-400">
                                <Users size={20} />
                            </div>
                            <span className="text-sm text-muted-foreground font-medium">סה"כ מטופלים</span>
                        </div>
                        <div className="text-2xl font-bold text-foreground">{totalPatients}</div>
                    </div>

                    <div className="bg-card p-4 rounded-2xl border border-border shadow-lg col-span-2 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="bg-red-500/20 p-3 rounded-xl text-red-400">
                                <AlertTriangle size={24} />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground font-medium">מטופלים בסיכון (ברמת קליניקה)</p>
                                <p className="text-2xl font-bold text-foreground">{totalHighRisk}</p>
                            </div>
                        </div>
                        {totalPatients > 0 && (
                            <div className="text-right">
                                <span className="text-xl font-bold text-red-400">
                                    {Math.round((totalHighRisk / totalPatients) * 100)}%
                                </span>
                                <p className="text-xs text-muted-foreground">מהכלל</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Therapists List */}
                <div>
                    <h3 className="text-foreground font-bold mb-4 flex items-center gap-2 text-lg">
                        <Users size={20} /> צוות המטפלים ({totalTherapists})
                    </h3>
                    
                    <div className="space-y-4">
                        {therapistsStats.map((stat) => (
                            <div key={stat.profile.id} className="bg-card p-4 rounded-2xl border border-border">
                                <div className="flex justify-between items-start mb-3">
                                    <div>
                                        <h4 className="text-foreground font-bold text-lg">{stat.profile.full_name || stat.profile.username || 'מטפל ללא שם'}</h4>
                                        <p className="text-xs text-muted-foreground mt-1">מטפל פעיל</p>
                                    </div>
                                    <div className="bg-muted px-3 py-1 rounded-full border border-border text-xs text-muted-foreground font-bold">
                                        {stat.patientCount} מטופלים
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-border">
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <TrendingUp size={16} className="text-emerald-400" />
                                        <span>ממוצע רצף: <strong className="text-foreground">{stat.avgStreak}</strong></span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <AlertTriangle size={16} className={stat.highRiskCount > 0 ? "text-red-400" : "text-muted-foreground"} />
                                        <span>סיכון מוגבר: <strong className={stat.highRiskCount > 0 ? "text-red-400" : "text-foreground"}>{stat.highRiskCount}</strong></span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ClinicAdminDashboard;
